import { c as _c } from "react/compiler-runtime";
import { useState, useId } from 'react';
import type { ReactNode, ElementType, SyntheticEvent } from 'react';
import { Accordion as MUIAccordion, AccordionDetails, AccordionSummary } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@classify/react-core-components/Box';
import { styled, SxProps, Theme } from '@classify/react-core-components/styles';
import type { SystemStyleObject } from '@mui/system';
import Typography from '../Typography';
import EnrichText from '../EnrichText';
export type AccordionItem = {
  title?: ReactNode;
  content?: ReactNode;
  disabled?: boolean;
  disableDefaultTitleStyles?: boolean;
  disableDefaultContentStyles?: boolean;
  titleSx?: SystemStyleObject<Theme>;
};
export interface Props {
  items: AccordionItem[];
  expandAriaLabel: string;
  defaultExpandedIndex?: number;
  sx?: SxProps<Theme>;
  slotProps?: {
    heading?: {
      component?: ElementType;
    };
  };
}
const AccordionStyled = styled(MUIAccordion)(({
  theme: {
    spacing,
    palette
  }
}) => ({
  '&.MuiAccordion-root': {
    ':before': {
      backgroundColor: palette.layout.surface.border
    }
  },
  '& .MuiAccordionSummary-root.Mui-expanded': {
    minHeight: 'auto'
  },
  '& .MuiAccordionSummary-content': {
    margin: spacing(2, 0)
  },
  '& ul': {
    margin: 0,
    paddingLeft: spacing(2)
  },
  '& li': {
    color: palette.text.onMainMinor
  }
}));
const Accordion = t0 => {
  const $ = _c(15);
  const {
    items,
    expandAriaLabel,
    defaultExpandedIndex,
    sx,
    slotProps
  } = t0;
  const mainId = useId();
  const [expandedPanelId, setExpandedPanelId] = useState(defaultExpandedIndex !== undefined ? `${mainId}-item-${defaultExpandedIndex}` : null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = panelId => (_e, expanded) => {
      setExpandedPanelId(expanded ? panelId : null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const getOnPanelChangeHandler = t1;
  const renderTitle = _temp;
  const renderContent = _temp2;
  let t2;
  if ($[1] !== expandAriaLabel || $[2] !== expandedPanelId || $[3] !== items || $[4] !== mainId || $[5] !== slotProps?.heading?.component) {
    let t3;
    if ($[7] !== expandAriaLabel || $[8] !== expandedPanelId || $[9] !== mainId || $[10] !== slotProps?.heading?.component) {
      t3 = (item_1, n) => {
        const id = `${mainId}-item-${n}`;
        return <AccordionStyled key={id} expanded={expandedPanelId === id} onChange={getOnPanelChangeHandler(id)} disabled={item_1.disabled} slotProps={{
          heading: {
            component: slotProps?.heading?.component || "div"
          }
        }}><AccordionSummary aria-controls={`content-${id}`} id={`header-${id}`} expandIcon={<ExpandMoreIcon aria-label={expandAriaLabel} />}>{renderTitle(item_1)}</AccordionSummary><AccordionDetails>{renderContent(item_1)}</AccordionDetails></AccordionStyled>;
      };
      $[7] = expandAriaLabel;
      $[8] = expandedPanelId;
      $[9] = mainId;
      $[10] = slotProps?.heading?.component;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = items.map(t3);
    $[1] = expandAriaLabel;
    $[2] = expandedPanelId;
    $[3] = items;
    $[4] = mainId;
    $[5] = slotProps?.heading?.component;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[12] !== sx || $[13] !== t2) {
    t3 = <Box sx={sx}>{t2}</Box>;
    $[12] = sx;
    $[13] = t2;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  return t3;
};
export default Accordion;
function _temp(item) {
  const {
    title,
    disableDefaultTitleStyles: shouldDisableDefaultTitleStyles,
    titleSx
  } = item;
  if (shouldDisableDefaultTitleStyles) {
    return title;
  }
  return <Typography component="div" sx={{
    color: "text.onMain",
    ...(titleSx || {})
  }}>{title}</Typography>;
}
function _temp2(item_0) {
  const {
    content,
    disableDefaultContentStyles: shouldDisableDefaultContentStyles
  } = item_0;
  if (shouldDisableDefaultContentStyles) {
    return content;
  }
  return <EnrichText><Typography component="div" sx={{
      color: "text.onMainMinor",
      whiteSpace: "break-spaces"
    }} {...typeof content === "string" ? {
      dangerouslySetInnerHTML: {
        __html: content
      }
    } : {
      children: content
    }} /></EnrichText>;
}